import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Table, Tabs, Tag, Button, Radio, Spin, Space, Tooltip, Typography, Descriptions, notification, InputRef } from "antd";
import { TableOutlined, UnorderedListOutlined, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { BASE_URL } from "../../../utils/constants";
import axios from "axios";
import { RecordedVehicles } from "utils/interfaces";
import { CommentOutlined, DeleteOutlined } from "@ant-design/icons";
import { FilterDropdownProps, TablePaginationConfig } from "antd/es/table/interface";
import Search from "antd/es/input/Search";
import Highlighter from "react-highlight-words";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import BasicDisplayModal from "../../GlobalComponents/Modals/BasicDisplayModal";
import styles from '../../../global.module.css'
import { useHasAction } from "../../../hooks/useHasAction";

const { Text } = Typography;

interface VerticalTab {
  key: string;
  tab: string;
}

interface VehicleModelTab {
  key: string;
  tab: string;
}

interface deleteRecordedFile {
  id: number,
  type: 'record' | 'file' | '',
}

const RecordDataView: React.FC = () => {
  const canDeleteRecordData: boolean = useHasAction("record_data_delete_records");
  const canDownloadRecordData: boolean = useHasAction("record_data_download_records");

  const [activeTab, setActiveTab] = useState<string>("");
  const [activeModelTab, setActiveModelTab] = useState<string>("");
  const [recordedVehicles, setRecordedVehicles] = useState<RecordedVehicles[]>([]);
  const [deleteRecord, setdeleteRecord] = useState<deleteRecordedFile>({id: 0, type: ''});
  const [viewMode, setViewMode] = useState<"tabs" | "table">("tabs");
  const [filterManufacturer, setFilterManufacturer] = useState<string | undefined>(undefined);
  const [filterModel, setFilterModel] = useState<string | undefined>(undefined);
  const [activeColumnFilters, setActiveColumnFilters] = useState<Record<string, any>>({});

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const [verticalTabs, setVerticalTabs] = useState<VerticalTab[]>([]);
  const [vehicleModels, setVehicleModels] = useState<VehicleModelTab[]>([]);
  const [downloadFormat, setDownloadFormat] = useState<string>("ASC");

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [commentContent, setCommentContent] = useState<string>('');

  const [loadingButtons, setLoadingButtons] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  const [api, contextHolder] = notification.useNotification();

  // antd's virtual Table needs a concrete pixel height (rc-virtual-list uses
  // it to compute how many rows to render) - a CSS `calc()` string works for
  // a plain scroll container but not here, so track the equivalent number.
  const [tableScrollY, setTableScrollY] = useState(() => window.innerHeight - 255);
  useEffect(() => {
    const onResize = () => setTableScrollY(window.innerHeight - 255);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fieldIncludes = (fieldValue: unknown, filterValue: boolean | React.Key): boolean =>
    fieldValue != null &&
    fieldValue.toString().toLowerCase().includes(String(filterValue).toLowerCase());

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void, close: () => void) => {
    clearFilters();
    setSearchText("");
    close();
  };

  // Plain text-box search filter, same pattern used across the other
  // pages in the app (VehicleListView, UsersView, etc).
  const getColumnSearchProps = (dataIndex: keyof RecordedVehicles) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Search
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onSearch={() => handleSearch(selectedKeys as string[], confirm, dataIndex as string)}
          style={{ marginBottom: 8, display: "flex" }}
          allowClear={{
            clearIcon: (
              <CloseOutlined onClick={() => clearFilters && handleReset(clearFilters, close)} />
            ),
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    // Controlled so the filter-chips row (see filterChips) can clear this
    // column's search from outside the column's own dropdown.
    filteredValue: activeColumnFilters[dataIndex as string] ?? null,
    onFilter: (value: boolean | React.Key, record: RecordedVehicles) => fieldIncludes(record[dataIndex], value),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text: any) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  // Same normal/data baudrate enumerations used by the vehicle config CAN
  // Bus widgets (car-config.schema.json) - recordings don't carry these as
  // separate fields, buad_rate_speed is one free-form string, so filtering
  // by one of these values matches it as a substring.
  const CAN_BAUDRATE_NORMAL_VALUES = ["125K", "250K", "500K", "1000K"];
  const CAN_BAUDRATE_DATA_VALUES = ["500K", "833K", "1M", "1M5", "2M", "3M", "4M", "5M", "6M7", "8M", "10M"];
  const baudRateFilters = [
    {
      text: "Normal Baud Rate",
      value: "__normal_group__",
      children: CAN_BAUDRATE_NORMAL_VALUES.map((v) => ({ text: v, value: v })),
    },
    {
      text: "Data Baud Rate",
      value: "__data_group__",
      children: CAN_BAUDRATE_DATA_VALUES.map((v) => ({ text: v, value: v })),
    },
  ];

  // Every distinct recording (functionality) name that appears across any
  // vehicle's recorded_file_list, for the Recorded Data column filter.
  const recordingNameOptions = useMemo(() => {
    const names = new Set<string>();
    recordedVehicles.forEach((v) =>
      (v.recorded_file_list || []).forEach((r: any) => {
        if (r?.functionality?.name) names.add(r.functionality.name);
      })
    );
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ text: name, value: name }));
  }, [recordedVehicles]);

  // One matcher per filterable column key, shared between each column's
  // own (self-contained) onFilter and matchesActiveColumnFilters below,
  // which the merged table view uses to pre-filter rows before computing
  // manufacturer/model rowSpans - see activeColumnFilters.
  const columnFilterMatchers: Record<string, (record: RecordedVehicles, value: boolean | React.Key) => boolean> = {
    canny_device_serial_no: (record, value) => fieldIncludes(record.canny_device_serial_no, value),
    created_at: (record, value) => fieldIncludes(record.created_at, value),
    vehicle_year: (record, value) => fieldIncludes(record.vehicle_year, value),
    type: (record, value) => fieldIncludes(record.type, value),
    buad_rate_speed: (record, value) => fieldIncludes(record.buad_rate_speed, value),
    recorded_file_list: (record, value) =>
      (record.recorded_file_list || []).some((r: any) => r?.functionality?.name === value),
    vehicle_name: (record, value) => record.vehicle_name === value,
    vehicle_model: (record, value) => record.vehicle_model === value,
  };

  const matchesActiveColumnFilters = (record: RecordedVehicles, filters: Record<string, any>): boolean =>
    Object.entries(filters).every(([key, selected]) => {
      if (!selected || !Array.isArray(selected) || selected.length === 0) return true;
      const matcher = columnFilterMatchers[key];
      if (!matcher) return true;
      return selected.some((value: React.Key) => matcher(record, value));
    });

  const handleTableFilterChange = (_pagination: TablePaginationConfig, filters: Record<string, any>) => {
    setActiveColumnFilters(filters);
  };

  // One color per filterable column - used both for that column's chips in
  // the title bar and for the little dot next to the column's own header,
  // so a chip and the header it came from are visually traceable at a
  // glance. Hex values (antd's own preset-color tokens) rather than preset
  // names, since the header dot is a plain CSS background and names like
  // "geekblue"/"volcano" aren't valid CSS colors - a hex string works both
  // as CSS and as a <Tag color="..."> value. Fixed per key (not
  // palette-index order) so a color stays stable even if columns reorder.
  const COLUMN_FILTER_COLORS: Record<string, string> = {
    vehicle_name: "#1677ff",
    vehicle_model: "#722ed1",
    canny_device_serial_no: "#13c2c2",
    created_at: "#52c41a",
    vehicle_year: "#eb2f96",
    type: "#fa541c",
    buad_rate_speed: "#2f54eb",
    recorded_file_list: "#faad14",
  };

  // Small colored dot appended to a column's title while it has an active
  // filter - same color as that column's chips in the title bar.
  const renderColumnTitle = (key: string, label: string) => (
    <Space size={6}>
      <span>{label}</span>
      {Array.isArray(activeColumnFilters[key]) && activeColumnFilters[key].length > 0 && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            display: "inline-block",
            background: COLUMN_FILTER_COLORS[key],
          }}
        />
      )}
    </Space>
  );

  const tableColumns = [
    {
      title: renderColumnTitle("canny_device_serial_no", "Canny Device Id"), dataIndex: "canny_device_serial_no", key: "canny_device_serial_no", width: 160,
      ...getColumnSearchProps("canny_device_serial_no"),
    },
    {
      title: renderColumnTitle("created_at", "Created At"), dataIndex: "created_at", key: "created_at", width: 180,
      ...getColumnSearchProps("created_at"),
    },
    {
      title: renderColumnTitle("vehicle_year", "Year"), dataIndex: "vehicle_year", key: "vehicle_year", width: 80,
      ...getColumnSearchProps("vehicle_year"),
    },
    {
      title: renderColumnTitle("type", "Type"), dataIndex: "type", key: "type", width: 100,
      ...getColumnSearchProps("type"),
    },
    {
      title: renderColumnTitle("buad_rate_speed", "Baud Rate"), dataIndex: "buad_rate_speed", key: "buad_rate_speed", width: 200,
      filters: baudRateFilters,
      filteredValue: activeColumnFilters.buad_rate_speed ?? null,
      onFilter: (value: boolean | React.Key, record: RecordedVehicles) => columnFilterMatchers.buad_rate_speed(record, value),
    },
    {
      title: renderColumnTitle("recorded_file_list", "Recorded Data"),
      dataIndex: "recorded_file_list",
      key: "recorded_file_list",
      width: 200,
      filters: recordingNameOptions,
      filteredValue: activeColumnFilters.recorded_file_list ?? null,
      onFilter: (value: boolean | React.Key, record: RecordedVehicles) => columnFilterMatchers.recorded_file_list(record, value),
      render: (recorded_file_list: any, vehicle: RecordedVehicles) => (
        <Space orientation="vertical" size={4}>
          {recorded_file_list.map((record: any) => {
            const key = `${vehicle.id}_${record.functionality.name}`;
            const hasComment = record.file.comment && record.file.comment.trim();
            return (
              <Space key={record.id} size={4}>
                {hasComment && (
                  <Tooltip title="View Comment">
                    <Button
                      type="text"
                      icon={<CommentOutlined style={{ fontSize: '16px' }} />}
                      onClick={() => {
                        setCommentContent(record.file.comment);
                        setShowCommentModal(true);
                      }}
                    />
                  </Tooltip>
                )}
                <Button
                  size="small"
                  loading={loadingStates[key] || false}
                  onClick={() => handleDownload(vehicle.id, record.functionality.name, vehicle)}
                  disabled={!canDownloadRecordData}
                >
                  {record.functionality.name}
                </Button>
                <Tooltip title="Delete File">
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setdeleteRecord({id: record.id, type: 'file'});
                      setShowDeleteModal(true);
                    }}
                    disabled={!canDeleteRecordData}
                  />
                </Tooltip>
              </Space>
            );
          })}
        </Space>
      )
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      width: 180,
      fixed: "right" as const,
      render: (id: number, record: RecordedVehicles) => (
        <Space wrap>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setdeleteRecord({id: id, type: 'record'});
              setShowDeleteModal(true);
            }}
            disabled={!canDeleteRecordData}
          >
            Delete
          </Button>
          {record.comment && record.comment.trim() && (
            <Tooltip title="View Comment">
              <Button
                icon={<CommentOutlined style={{ fontSize: '20px' }} />}
                onClick={() => {
                  setCommentContent(record.comment);
                  setShowCommentModal(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      )
    },
  ];

  const manufacturerOptions = useMemo(
    () =>
      Array.from(new Set(recordedVehicles.map((v) => v.vehicle_name)))
        .sort((a, b) => a.localeCompare(b))
        .map((manufacturer) => ({ value: manufacturer, label: manufacturer })),
    [recordedVehicles]
  );

  const manufacturerColumnFilters = useMemo(
    () => manufacturerOptions.map((o) => ({ text: o.label, value: o.value })),
    [manufacturerOptions]
  );

  const modelColumnFilters = useMemo(() => {
    const selectedManufacturers: string[] | undefined = activeColumnFilters.vehicle_name;
    const source = recordedVehicles.filter((v) => {
      if (filterManufacturer && v.vehicle_name !== filterManufacturer) return false;
      if (selectedManufacturers && selectedManufacturers.length > 0 && !selectedManufacturers.includes(v.vehicle_name)) return false;
      return true;
    });
    return Array.from(new Set(source.map((v) => v.vehicle_model)))
      .sort((a, b) => a.localeCompare(b))
      .map((model) => ({ text: model, value: model }));
  }, [recordedVehicles, filterManufacturer, activeColumnFilters.vehicle_name]);

  const handleManufacturerFilterChange = (value: string | undefined) => {
    setFilterManufacturer(value);
    // A manufacturer change can invalidate the currently selected model -
    // and clearing the manufacturer entirely leaves no manufacturer for
    // the model to belong to, so drop it too.
    if (!value || (filterModel && !recordedVehicles.some((v) => v.vehicle_name === value && v.vehicle_model === filterModel))) {
      setFilterModel(undefined);
    }
  };

  const COLUMN_FILTER_LABELS: Record<string, string> = {
    vehicle_name: "Manufacturer",
    vehicle_model: "Model",
    canny_device_serial_no: "Canny Device Id",
    created_at: "Created At",
    vehicle_year: "Year",
    type: "Type",
    buad_rate_speed: "Baud Rate",
    recorded_file_list: "Recorded Data",
  };

  const removeColumnFilterValue = (key: string, value: any) => {
    setActiveColumnFilters((prev) => {
      const current = prev[key];
      if (!Array.isArray(current)) return prev;
      const next = current.filter((v) => v !== value);
      const updated = { ...prev };
      if (next.length > 0) {
        updated[key] = next;
      } else {
        delete updated[key];
      }
      return updated;
    });
    if (searchedColumn === key) {
      setSearchText("");
      setSearchedColumn("");
    }
  };

  // Every currently applied filter, from both the cascading Selects and
  // the column filters, as one flat list of removable chips - shown in the
  // card title (see the filter-chips row below).
  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; color: string; onRemove: () => void }[] = [];
    if (filterManufacturer) {
      chips.push({
        key: "select-manufacturer",
        label: `Manufacturer: ${filterManufacturer}`,
        color: COLUMN_FILTER_COLORS.vehicle_name,
        onRemove: () => handleManufacturerFilterChange(undefined),
      });
    }
    if (filterModel) {
      chips.push({
        key: "select-model",
        label: `Model: ${filterModel}`,
        color: COLUMN_FILTER_COLORS.vehicle_model,
        onRemove: () => setFilterModel(undefined),
      });
    }
    Object.entries(activeColumnFilters).forEach(([key, values]) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => {
        chips.push({
          key: `${key}-${value}`,
          label: `${COLUMN_FILTER_LABELS[key] ?? key}: ${value}`,
          color: COLUMN_FILTER_COLORS[key] ?? "default",
          onRemove: () => removeColumnFilterValue(key, value),
        });
      });
    });
    return chips;
  }, [filterManufacturer, filterModel, activeColumnFilters, searchedColumn]);// eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setActiveColumnFilters({});
    setFilterManufacturer(undefined);
    setFilterModel(undefined);
    setSearchText("");
    setSearchedColumn("");
  };

  // Sorted by manufacturer then model so same-valued rows land next to each
  // other, which is what the rowSpan merge below depends on.
  const filteredSortedVehicles = useMemo(
    () =>
      recordedVehicles
        .filter(
          (v) =>
            (!filterManufacturer || v.vehicle_name === filterManufacturer) &&
            (!filterModel || v.vehicle_model === filterModel) &&
            matchesActiveColumnFilters(v, activeColumnFilters)
        )
        .slice()
        .sort(
          (a, b) =>
            a.vehicle_name.localeCompare(b.vehicle_name) || a.vehicle_model.localeCompare(b.vehicle_model)
        ),
    [recordedVehicles, filterManufacturer, filterModel, activeColumnFilters]// eslint-disable-line react-hooks/exhaustive-deps
  );

  // rowSpan for the merged table: the first row of each contiguous
  // manufacturer/model run carries the full run length, every other row in
  // that run carries 0 (antd's convention for "merged into the cell above").
  const mergedTableRows = useMemo(() => {
    const rows = filteredSortedVehicles;
    const manufacturerRowSpans = new Array(rows.length).fill(0);
    const modelRowSpans = new Array(rows.length).fill(0);
    let manufacturerStart = 0;
    let modelStart = 0;
    for (let i = 0; i <= rows.length; i++) {
      if (i === rows.length || rows[i].vehicle_name !== rows[manufacturerStart].vehicle_name) {
        manufacturerRowSpans[manufacturerStart] = i - manufacturerStart;
        manufacturerStart = i;
      }
      if (
        i === rows.length ||
        rows[i].vehicle_name !== rows[modelStart].vehicle_name ||
        rows[i].vehicle_model !== rows[modelStart].vehicle_model
      ) {
        modelRowSpans[modelStart] = i - modelStart;
        modelStart = i;
      }
    }
    return rows.map((row, idx) => ({
      ...row,
      __manufacturerRowSpan: manufacturerRowSpans[idx],
      __modelRowSpan: modelRowSpans[idx],
    }));
  }, [filteredSortedVehicles]);

  type MergedRow = (typeof mergedTableRows)[number];

  const mergedTableColumns = [
    {
      title: renderColumnTitle("vehicle_name", "Manufacturer"),
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      width: 160,
      fixed: "left" as const,
      filters: manufacturerColumnFilters,
      filteredValue: activeColumnFilters.vehicle_name ?? null,
      onFilter: (value: boolean | React.Key, record: MergedRow) => columnFilterMatchers.vehicle_name(record, value),
      onCell: (record: MergedRow) => ({ rowSpan: record.__manufacturerRowSpan }),
    },
    {
      title: renderColumnTitle("vehicle_model", "Model"),
      dataIndex: "vehicle_model",
      key: "vehicle_model",
      width: 160,
      fixed: "left" as const,
      filters: modelColumnFilters,
      filteredValue: activeColumnFilters.vehicle_model ?? null,
      onFilter: (value: boolean | React.Key, record: MergedRow) => columnFilterMatchers.vehicle_model(record, value),
      onCell: (record: MergedRow) => ({ rowSpan: record.__modelRowSpan }),
    },
    ...tableColumns,
  ];

  useEffect(() => {
    updateCannyDevices();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const updateCannyDevices = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}recorded_vehicles`)
      .then((response) => {
        if(response.data.content.length === 0) {
          setLoading(false);
        } else {
          const vehicles: RecordedVehicles[] = response.data.content;
          setRecordedVehicles(vehicles);

          const manufacturers = Array.from(
            new Set(vehicles.map((vehicle) => vehicle.vehicle_name))
          );
          const tabs: VerticalTab[] = manufacturers.map((manufacturer) => ({
            key: manufacturer,
            tab: manufacturer,
          }));

          setVerticalTabs(tabs);

          const newActiveTab = tabs.some(tab => tab.key === activeTab) ? activeTab : tabs[0]?.key;
          setActiveTab(newActiveTab);

          const models = Array.from(
            new Set(
              vehicles
                .filter((device) => device.vehicle_name === newActiveTab)
                .map((device) => device.vehicle_model)
            )
          );

          const modelTabs: VehicleModelTab[] = models.map((model) => ({
            key: model,
            tab: model,
          }));

          setVehicleModels(modelTabs);

          const newActiveModelTab = models.includes(activeModelTab) ? activeModelTab : models[0];
          setActiveModelTab(newActiveModelTab);
        }
      })
      .catch((error) => {
        console.error("Error fetching Canny Devices:", error);
      })
      .finally(() => setLoading(false));
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const models = Array.from(
      new Set(
        recordedVehicles
          .filter((device) => device.vehicle_name === key)
          .map((device) => device.vehicle_model)
      )
    );

    const modelTabs: VehicleModelTab[] = models.map((model) => ({
      key: model,
      tab: model,
    }));

    setVehicleModels(modelTabs);
    setActiveModelTab(models[0]);
    // Manually picking a tab breaks the link to whatever manufacturer/model
    // was filtered in Table view - switching back there should start those
    // two filters fresh instead of silently re-showing the old ones (see
    // the tabs<-filters sync effect below for the other direction).
    setFilterManufacturer(undefined);
    setFilterModel(undefined);
  };

  const handleModelTabChange = (key: string) => {
    setActiveModelTab(key);
    setFilterManufacturer(undefined);
    setFilterModel(undefined);
  };

  // Table view's manufacturer/model filters drive which tab opens when
  // switching into Tabs view (but not the other way - see handleTabChange/
  // handleModelTabChange, which clear these two on manual tab navigation
  // so the link only survives if the user hasn't actually diverged). Either
  // the cascading Select (filterManufacturer/filterModel, a single value)
  // or the Manufacturer/Model column checklists (activeColumnFilters, which
  // can hold several) can drive this - the Select wins if both happen to be
  // set, and a multi-selected column filter just opens its first value.
  useEffect(() => {
    if (viewMode !== "tabs") return;
    const targetManufacturer = filterManufacturer ?? activeColumnFilters.vehicle_name?.[0];
    if (!targetManufacturer) return;
    const targetModel = filterModel ?? activeColumnFilters.vehicle_model?.[0];
    setActiveTab(targetManufacturer);
    const models = Array.from(
      new Set(
        recordedVehicles
          .filter((device) => device.vehicle_name === targetManufacturer)
          .map((device) => device.vehicle_model)
      )
    );
    setVehicleModels(models.map((model) => ({ key: model, tab: model })));
    setActiveModelTab(targetModel && models.includes(targetModel) ? targetModel : models[0]);
  }, [viewMode]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteRecord = (recordId: number) => {
    setLoadingButtons(true);
    axios
      .post(`${BASE_URL}delete_record/${recordId}`)
      .then(() => {
        updateCannyDevices();
      })
      .finally(() => {
        setLoadingButtons(false);
        setShowDeleteModal(false)
        setdeleteRecord({id: 0, type: ''});
        api.success({
          title: 'Delete Successful',
          description: `Record deleted successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error deleting record:", error);
        api.error({
          title: 'Delete Failed',
          description: `Failed to delete record.`,
          placement: 'bottomRight',
          showProgress: true
        });
      });
  }

  const handleDeleteRecordFile = (fileId: number) => {
    setLoadingButtons(true);
    axios
      .post(`${BASE_URL}delete_recorded_file/${fileId}`)
      .then(() => {
        updateCannyDevices();
      })
      .finally(() => {
        setLoadingButtons(false);
        setShowDeleteModal(false)
        setdeleteRecord({id: 0, type: ''});
        api.success({
          title: 'Delete Successful',
          description: `Recorded file deleted successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error deleting recorded file:", error);
        api.error({
          title: 'Delete Failed',
          description: `Failed to delete recorded file.`,
          placement: 'bottomRight',
          showProgress: true
        });
      });
  }

  const handleDownload = (id:number, functionalityName:string, vehicle: RecordedVehicles) => {
    const key = `${id}_${functionalityName}`;
    setLoadingStates((prev) => ({ ...prev, [key]: true }));

    axios
      .get(`${BASE_URL}download_recorded_vehicle_data/${id}/${functionalityName}`, {
        params: { format: downloadFormat.toLowerCase() },
        responseType: "blob",
      })
      .then((response) => {
        const href = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", `${vehicle.canny_device_serial_no}_${vehicle.vehicle_name}_${vehicle.vehicle_model}_${vehicle.vehicle_year}_${functionalityName}.${downloadFormat.toLowerCase()}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        setLoadingStates((prev) => ({ ...prev, [key]: false }));
        api.success({
          title: 'Download Successful',
          description: `File for ${vehicle.canny_device_serial_no} - ${functionalityName} downloaded successfully.`,
          placement: 'bottomRight',
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        setLoadingStates((prev) => ({ ...prev, [key]: false }));
        api.error({
          title: 'Download Failed',
          description: `Failed to download file for ${vehicle.canny_device_serial_no} - ${functionalityName}.`,
          placement: 'bottomRight',
          showProgress: true
        });
      });
  };

  const verticalTabItems = verticalTabs.map((vTab) => ({
    label: vTab.tab,
    key: vTab.key,
  }));

  const modelTabItems = vehicleModels.map((modelTab) => ({
    label: modelTab.tab,
    key: modelTab.key,
  }));

  const titleDeleteModal = () => {
    if(deleteRecord.type === "file"){
      const filesRecord = recordedVehicles
      .flatMap((vehicle) =>
        vehicle.recorded_file_list.map((file:any) => ({
          ...file,
          vehicle,
        }))
      )
      .find((file) => file.id === deleteRecord.id);

      if (filesRecord){
      const { vehicle } = filesRecord;
      return (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Vehicle">{vehicle.vehicle_name} - {vehicle.vehicle_model}</Descriptions.Item>
          <Descriptions.Item label="Canny ID">{vehicle.canny_device_serial_no}</Descriptions.Item>
          <Descriptions.Item label="Type">{vehicle.type}</Descriptions.Item>
          <Descriptions.Item label="Baud Rate">{vehicle.buad_rate_speed}</Descriptions.Item>
          <Descriptions.Item label="Functionality">{filesRecord.functionality.name}</Descriptions.Item>
        </Descriptions>
      );
      }
    }
    if(deleteRecord.type === "record"){
      const vehicleRecord = recordedVehicles.find(
        (vehicle) => vehicle.id === deleteRecord.id
      );

      if (vehicleRecord){
        return (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Vehicle">{vehicleRecord.vehicle_name} - {vehicleRecord.vehicle_model}</Descriptions.Item>
            <Descriptions.Item label="Canny ID">{vehicleRecord.canny_device_serial_no}</Descriptions.Item>
            <Descriptions.Item label="Type">{vehicleRecord.type}</Descriptions.Item>
            <Descriptions.Item label="Baud Rate">{vehicleRecord.buad_rate_speed}</Descriptions.Item>
          </Descriptions>
        )
      }
    }
  }

  return (
    <div>
      {contextHolder}
      <div>
        <Card
          className={styles.globalCard}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={{textAlign: 'left', flexShrink: 0 }}>Recorded Vehicles</span>
              {filterChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, padding: "2px 0" }}>
                    {filterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        color={chip.color}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          chip.onRemove();
                        }}
                        style={{ flexShrink: 0 }}
                      >
                        {chip.label}
                      </Tag>
                    ))}
                  </div>
                  <Button size="small" type="link" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                </div>
              )}
              <span style={{textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: filterChips.length > 0 ? 0 : 'auto' }}>
              <Tooltip title={viewMode === "tabs" ? "Switch to single table view" : "Switch to tabs view"}>
                <Button
                  icon={viewMode === "tabs" ? <TableOutlined /> : <UnorderedListOutlined />}
                  onClick={() => setViewMode(viewMode === "tabs" ? "table" : "tabs")}
                >
                  {viewMode === "tabs" ? "Table View" : "Tabs View"}
                </Button>
              </Tooltip>
              <Text type="secondary">Download format:</Text>
              <Radio.Group
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="JSON">JSON</Radio.Button>
                <Radio.Button value="ASC">ASC</Radio.Button>
              </Radio.Group>
              </span>
            </div>
          }
        >
          <Spin size="large" spinning={loading}>
            {viewMode === "tabs" ? (
              <div style={{ display: "flex" }}>
                <Tabs
                  tabPlacement="start"
                  items={verticalTabItems}
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  style={{ flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {verticalTabItems.map(
                    (vTab) =>
                      activeTab === vTab.key && (
                        <div key={vTab.key}>
                          <Tabs
                            items={modelTabItems}
                            activeKey={activeModelTab}
                            onChange={handleModelTabChange}
                          />
                          <Table
                            pagination={false}
                            columns={tableColumns}
                            dataSource={recordedVehicles.filter(
                              (device) =>
                                device.vehicle_name === vTab.key &&
                                device.vehicle_model === activeModelTab
                            )}
                            onChange={handleTableFilterChange}
                            scroll={{x : 1200, y: tableScrollY}}
                            virtual
                            rowKey="id"
                            className='horizontally-synced-table'
                          />
                        </div>
                      )
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Table
                  pagination={false}
                  columns={mergedTableColumns}
                  dataSource={mergedTableRows}
                  onChange={handleTableFilterChange}
                  scroll={{x : 1520, y: tableScrollY}}
                  virtual
                  rowKey="id"
                  className='horizontally-synced-table'
                />
              </div>
            )}
          </Spin>
        </Card>
      </div>
      <DeleteModal
        showModal={showDeleteModal}
        onDelete={() => {
          if (deleteRecord.type === 'file') {
            handleDeleteRecordFile(deleteRecord.id);
          } else if (deleteRecord.type === 'record') {
            handleDeleteRecord(deleteRecord.id);
          }
        }}
        onClose={() => {
          setShowDeleteModal((loadingButtons) ? true : false);
          if(!loadingButtons){
          setdeleteRecord({id: 0, type: ''})
          }
        }}
        title={deleteRecord.type === 'file' ? "You are about to delete this file?" : "You are about to delete this record?"}
        content={titleDeleteModal()}
        loadingDelete={loadingButtons}
        disableDelete={loadingButtons}
        disabledCancel={loadingButtons}
      />
      <BasicDisplayModal
        showModal={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        content={<p>{commentContent}</p>}
        title="Comment"
      />
    </div>
  );
};

export default RecordDataView;
